// Third-party
import React from 'react';

// Components
import GroupInfoStep from './GroupInfoStep';
import { UnitInput } from '../../../inputs';
import { HoldingIcon } from '../../../common';
import { ContactInitialsLabel } from '../../../misc';

// Utils
import { formatLocalizedString, formatMessage } from '../../../../../utils/i18n';
import ComparisonItem from '@/types/comparison-item';
import { Inline } from '@capintel/component-lib/inline';
import { SectionBanner } from '@capintel/component-lib/section-banner';
import { Stack } from '@capintel/component-lib/stack';

type PartialComparisonGroupProduct = {
  itemId: string;
  dollars?: number;
  fee?: number;
};

type Props = {
  name: string;
  groupColor: string;
  selectedItems: ComparisonItem[];
  selectedItemDetails: PartialComparisonGroupProduct[];
  onNameChange: (name: string) => void;
  onValueChange: (value: string, id: string) => void;
  onFeeChange: (value: string, id: string) => void;
  isEditing?: boolean;
};

const GroupValuesStep = (props: Props) => {
  const { name, selectedItems, selectedItemDetails, onNameChange, onValueChange, onFeeChange, groupColor } = props;

  return (
    <div className="group-values-step">
      <Stack gap="200">
        {!props.isEditing && (
          <Inline gap="200" align="center">
            <div className="group-color" style={{ backgroundColor: groupColor }} />
            <GroupInfoStep compact name={name} onNameChange={onNameChange} editable />
          </Inline>
        )}

        <SectionBanner variant="warning" content={formatMessage('AddComparisonGroupModal.GroupValuesStep.WarningMessage')} />

        <div className="table title-table">
          <div className="title">{formatMessage('AddComparisonGroupModal.GroupValuesStep.Table.Title.Item')}</div>
          <div className="title">{formatMessage('AddComparisonGroupModal.GroupValuesStep.Table.Title.Value')}</div>
          <div className="title">{formatMessage('AddComparisonGroupModal.GroupValuesStep.Table.Title.AdvisoryFee')}</div>
        </div>
        <div className="table value-table">
          {selectedItems.map(item => {
            const details = selectedItemDetails.find(itemDetails => itemDetails.itemId === item._id);
            const roundedSize: number | undefined = details?.dollars ? +details.dollars.toFixed(2) : undefined;
            const itemContact = item.contactsInfo ? item.contactsInfo[0] : null;
            const output = details?.itemId ? (
              <React.Fragment key={item._id}>
                <div data-private data-dd-privacy="mask" className="item">
                  <div className="item-info">
                    <HoldingIcon type={item.type} color={item.color} />
                    <span className="item-name">{formatLocalizedString(item.name)}</span>
                  </div>
                  {itemContact && <ContactInitialsLabel contact={itemContact} />}
                </div>

                <UnitInput
                  onValueChange={(value: string) => onValueChange(value, details.itemId)}
                  id={details.itemId}
                  type="number"
                  compact
                  className="item"
                  placeholder={formatMessage('AddComparisonGroupModal.GroupValuesStep.Table.Placeholder.Dollars')}
                  value={roundedSize}
                  unitSymbol="$"
                  min={0}
                  error={details.dollars !== undefined && details.dollars <= 0}
                />

                <UnitInput
                  onValueChange={(value: string) => onFeeChange(value, details.itemId)}
                  id={details.itemId}
                  type="number"
                  compact
                  className="item"
                  value={details.fee}
                  placeholder={formatMessage('AddComparisonGroupModal.GroupValuesStep.Table.Placeholder.Fee')}
                  unitSymbol={formatMessage('AddComparisonGroupModal.GroupValuesStep.Table.Unit.AdvisoryFee')}
                  min={0}
                />
              </React.Fragment>
            ) : null;
            return output;
          })}
        </div>
      </Stack>
    </div>
  );
};

export default GroupValuesStep;
